import { useEffect, useState } from 'react';
import { Copy, ChevronDown, ChevronRight, Trash2, Globe } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Button, Input, Select, Badge, Dialog } from '../../ui';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';
import { actualizarAlumno, eliminarAlumno } from '../api';
import { useFormAuth } from '../../laeformacion/FormacionAccess';

const ESTADOS = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'contactado', label: 'Contactado' },
  { value: 'matriculado', label: 'Matriculado' },
  { value: 'descartado', label: 'Descartado' },
];
const MAX_NOTAS = 5000;

const fmtFecha = (d) => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—');

function Campo({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-800">{children || '—'}</div>
    </div>
  );
}

export default function SepeAlumnoPotencialDialog({ open, alumno, onClose, onSaved, onDeleted }) {
  const { can } = useFormAuth();
  const canManage = can('formacion.sepe.alumnos.manage');

  const [estado, setEstado] = useState('nuevo');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [tecnicoAbierto, setTecnicoAbierto] = useState(false);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);

  useEffect(() => {
    if (open && alumno) {
      setEstado(alumno.estado || 'nuevo');
      setNotas(alumno.notas || '');
      setTecnicoAbierto(false);
      setConfirmarBorrado(false);
    }
  }, [open, alumno]);

  if (!alumno) return null;

  const copiarEmail = async () => {
    try { await navigator.clipboard.writeText(alumno.email); toast.success('Email copiado'); }
    catch { toast.error('No se pudo copiar'); }
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const actualizado = await actualizarAlumno(alumno.id, { estado, notas });
      toast.success('Cambios guardados');
      onSaved?.(actualizado);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const borrar = async () => {
    try {
      await eliminarAlumno(alumno.id);
      setConfirmarBorrado(false);
      toast.success('Alumno eliminado');
      onDeleted?.(alumno.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        size="lg"
        title={`${alumno.nombre} ${alumno.apellidos}`}
        description="Alumno potencial · Cursos SEPE"
        footer={<Button variant="secondary" onClick={onClose}>Cerrar</Button>}
      >
        <div className="space-y-5">
          {/* Cabecera contacto */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <a href={`mailto:${alumno.email}`} className="text-sm font-medium text-[#ea580c] hover:underline">{alumno.email}</a>
            <button type="button" onClick={copiarEmail} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
              <Copy className="w-3.5 h-3.5" /> Copiar email
            </button>
            <span className="text-sm text-slate-600">{alumno.telefono}</span>
          </div>

          {/* Datos del lead */}
          <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
            <Campo label="Curso">{alumno.curso?.nombre}</Campo>
            <Campo label="Convocatoria">{alumno.curso?.convocatoria?.nombre}</Campo>
            <Campo label="Área">{alumno.area?.nombre}</Campo>
            <Campo label="Provincia">{alumno.provincia}</Campo>
            <Campo label="Situación laboral">{alumno.situacion_laboral}</Campo>
            <Campo label="Cómo nos conoció">{alumno.como_nos_conocio}</Campo>
            <Campo label="Fecha de inscripción">{fmtFecha(alumno.created_at || alumno.createdAt)}</Campo>
          </div>

          {/* Estado y seguimiento */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Estado y seguimiento</h3>
            <div className="grid gap-3">
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                <Select value={estado} onChange={(e) => setEstado(e.target.value)} disabled={!canManage}>
                  {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas internas</label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value.slice(0, MAX_NOTAS))}
                  disabled={!canManage}
                  rows={4}
                  placeholder="Seguimiento del lead (llamadas, documentación, etc.)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f97316]/40 disabled:bg-slate-50"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{notas.length}/{MAX_NOTAS}</p>
              </div>
              {canManage && (
                <div>
                  <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</Button>
                </div>
              )}
            </div>
          </div>

          {/* Datos técnicos (colapsable) */}
          <div className="border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setTecnicoAbierto((v) => !v)} className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900">
              {tecnicoAbierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Datos técnicos
            </button>
            {tecnicoAbierto && (
              <div className="mt-2 grid gap-2">
                <Campo label="IP de origen">{alumno.ip_origen}</Campo>
                <Campo label="User agent"><span className="break-all">{alumno.user_agent}</span></Campo>
                <p className="text-xs text-slate-400">Datos almacenados para diagnóstico técnico y detección de bots.</p>
              </div>
            )}
          </div>

          {/* Eliminar */}
          {canManage && (
            <div className="border-t border-slate-100 pt-4">
              <Button variant="danger" onClick={() => setConfirmarBorrado(true)}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar alumno potencial
              </Button>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmacionEliminar
        open={confirmarBorrado}
        onClose={() => setConfirmarBorrado(false)}
        onConfirm={borrar}
        titulo="Eliminar alumno potencial"
        descripcion="Esta acción no se puede deshacer. Se borrarán todos los datos del lead."
        palabraConfirmacion="ELIMINAR"
        textoBotonConfirmar="Eliminar definitivamente"
      />
    </>
  );
}
